/**
 * CC-003 / US-155 + US-156 — Growth analytics service.
 * Thin typed wrappers over the admin-only analytics endpoints
 * (LP src/routes/funnel.js + src/routes/mrr-churn.js, mounted at /api/analytics).
 * Responses are { success, data } — wrappers unwrap to `data` and surface
 * backend {success:false,message} as a rejected promise.
 */
import { adminApi } from './api';

export interface FunnelStage {
    name: string;
    count: number;
    conversionRate: number;
    dropOff: number;
}
export interface FunnelSource {
    source: string;
    conversionRate: number;
    signups: number;
    committed: number;
}
export interface FunnelData {
    totalUsers: number;
    stages: FunnelStage[];
    bySource?: FunnelSource[];
}
export interface MrrData {
    total: number;
    byPlan: Record<string, number>;
    trend: number;
}
export interface ChurnPlan {
    activeAtStart: number;
    cancelled: number;
    rate: number;
}
export interface ChurnData {
    byPlan: Record<string, ChurnPlan>;
    overallRate: number;
    trend: number;
}
export interface Cohort {
    cohortMonth: string;
    size: number;
    retentionRate: number[];
    mrrPerUser?: number;
}

const unwrap = (res: any, label: string): any => {
    const body = res?.data;
    if (body && body.success === false) {
        return Promise.reject(new Error(body.message || `${label} failed`));
    }
    return body?.data !== undefined ? body.data : body;
};

export const growthAnalytics = {
    getFunnel: (start: string, end: string, source?: string): Promise<FunnelData> =>
        adminApi
            .get('api/analytics/funnel', { params: { start, end, source } })
            .then((r: any) => unwrap(r, 'funnel')),

    getMrr: (params?: { start?: string; end?: string }): Promise<MrrData> =>
        adminApi.get('api/analytics/mrr', { params }).then((r: any) => unwrap(r, 'mrr')),

    getChurn: (params?: { start?: string; end?: string }): Promise<ChurnData> =>
        adminApi.get('api/analytics/churn', { params }).then((r: any) => unwrap(r, 'churn')),

    getCohorts: (params?: { start?: string; end?: string; months?: number }): Promise<Cohort[]> =>
        adminApi.get('api/analytics/cohorts', { params }).then((r: any) => unwrap(r, 'cohorts')),
};

export default growthAnalytics;
